import { getGiveback2023IntroText } from '@/components/Giveback2023/text';
import Layout from '@/components/shared/Layout';
import { SEO } from '@/components/shared/SEO';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/giveback_2023/ogImage.png';
import { useHeaderLayout } from '@/store/useHeaderLayout';
import { GIVEBACK_LANDING_PAGE } from '@/utils/productAdaPages';
import type { PageProps } from '@/utils/types';
import { ThemeProvider } from '@lemonade-hq/cdk';
import { themes } from '@lemonade-hq/nuis';
import type { NextPage, NextPageContext } from 'next';
import dynamic from 'next/dynamic';
import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';

const Giveback2023Index = dynamic(import('../components/Giveback2023/Giveback2023Index'), {
  ssr: false,
});

const GlobalHTMLOverrides = createGlobalStyle`
    html {
        background-color: #202020!important;
    }

    header {
        background: transparent!important;
        border: none !important;

        nav a {
            color: white !important;
        }

        a[aria-label*="Lemonade"] div {
            background-color: #4a4a4a!important;
        }

        [role="menu"] {
            button path {
                stroke: white!important;
            }
        }
    }
`;

const Container = styled.div`
  min-height: 100vh;
`;

interface Giveback2023PageProps extends PageProps {
  readonly content: string;
  readonly causeName?: string;
  readonly causeCategoryName?: string;
  readonly isGuest: boolean;
}

const themeOverrides = {
  colors: {
    ...themes.pinkish.colors,
    white: '#202020',
    night: '#ffffff',
    light: '#202020',
  },
};

const SHARING_DETAILS = {
  event: 'Giveback 2023 share',
  title: 'Lemonade Giveback 2023',
  content: "Have you seen the Lemonade Giveback 2023? It's amazing. Check it out 🤩",
};

const Giveback2023Page: NextPage<Giveback2023PageProps> = ({ content, causeName, causeCategoryName, isGuest }) => {
  const setState = useHeaderLayout(s => s.setState);

  React.useEffect(() => {
    setState({
      showLogin: false,
    });
  }, [setState]);

  return (
    <ThemeProvider themeName="pinkish" themes={themes} overrides={themeOverrides}>
      <GlobalHTMLOverrides />
      <Layout
        {...sharedConfig['en-US'].layout}
        pt={[30, 30, 30]}
        showCTA={false}
        showUserAccountComponent={false}
        overflow="visible"
        page={GIVEBACK_LANDING_PAGE}
        contentBackground={themeOverrides.colors.white}
        stickyHeader={false}
        fixedHeader
        showFooter={false}
        showBackToYourQuote={false}
      >
        <SEO
          title="The Lemonade Community 2023 Giveback"
          metaTitle="The Lemonade Community 2023 Giveback"
          description="Who knew insurance could be so impactful? 58 nonprofits from around the world receive donations from Lemonade, in the 2023 annual Giveback."
          ogImage={ogImage.src}
        />
        <Container>
          <Giveback2023Index
            content={content}
            sharingDetails={SHARING_DETAILS}
            causeName={causeName}
            causeCategoryName={causeCategoryName}
            isGuest={isGuest}
          />
        </Container>
      </Layout>
    </ThemeProvider>
  );
};

Giveback2023Page.getInitialProps = async (ctx: NextPageContext): Promise<Giveback2023PageProps> => {
  const { content, causeCategoryName, causeName, isGuest } = getGiveback2023IntroText(ctx.query);

  return {
    content,
    causeName,
    causeCategoryName,
    isGuest,
    hideCookieConsent: true,
  };
};

export default Giveback2023Page;
